import {BaseCommand} from './base.command';
import {CommentSnapshot} from '../interfaces/command.interface';
import {CommentService} from '../../../api/services/comment.service';
import {firstValueFrom} from 'rxjs';
import {UndoLabelMetadata, UndoObjectType, UndoVerb} from '../undo-label.utils';
import {CommentsStateManager} from '../../../shared/components/comments/comments.state-manager';

type CommentAction = 'add' | 'edit' | 'remove';

/**
 * CommentCommand handles comment operations with undo/redo support.
 * Supports: add, edit, and remove operations.
 */
export class CommentCommand extends BaseCommand<CommentSnapshot> {
  private action: CommentAction;
  private createdCommentId?: string; // Tracks the ID of newly created comments

  constructor(
    private commentService: CommentService,
    private commentsStateManager: CommentsStateManager,
    action: CommentAction,
    snapshot: CommentSnapshot
  ) {
    // Determine object type based on entity type
    let objectType: UndoObjectType;
    switch (snapshot.entityType) {
      case 'node':
        objectType = 'Node';
        break;
      case 'link':
        objectType = 'Link';
        break;
      case 'map':
        objectType = 'Smap';
        break;
    }

    // Determine verb based on action
    let verb: UndoVerb;
    switch (action) {
      case 'add':
        verb = 'Addition';
        break;
      case 'edit':
        verb = 'Change';
        break;
      case 'remove':
        verb = 'Removal';
        break;
    }

    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: verb,
      part: 'Comment'
    };

    super(
      'comment',
      `${objectType} Comment ${verb}`,
      {before: snapshot, after: snapshot},
      labelMetadata
    );

    this.action = action;
    this.createdCommentId = snapshot.commentId;
  }

  async execute(): Promise<void> {
    const snapshot = this.snapshot.after;

    switch (this.action) {
      case 'add':
        // Re-create the comment (for redo)
        if (snapshot.parentId) {
          // This is a reply
          const result = await firstValueFrom(
            this.commentService.replyToComment(snapshot.parentId, snapshot.content)
          );
          this.createdCommentId = result.id;
        } else {
          // This is a new top-level comment
          const result = await firstValueFrom(
            this.commentService.addComment(
              snapshot.entityId,
              snapshot.entityType,
              snapshot.content
            )
          );
          this.createdCommentId = result.id;
        }
        // Update canvas icon if this was the first comment
        if (snapshot.wasFirstComment) {
          this.commentsStateManager.triggerFirstCommentAdded({
            elementId: snapshot.entityId,
            elementType: snapshot.entityType
          });
        }
        break;

      case 'edit':
        // Apply the new content
        if (snapshot.commentId) {
          await firstValueFrom(
            this.commentService.editComment(snapshot.commentId, snapshot.content)
          );
        }
        break;

      case 'remove':
        // Delete the comment
        const commentIdToDelete = this.createdCommentId || snapshot.commentId;
        if (commentIdToDelete) {
          await firstValueFrom(
            this.commentService.deleteComment(commentIdToDelete)
          );
        }
        // Update canvas icon if this was the last comment
        if (snapshot.wasLastComment) {
          this.commentsStateManager.triggerNoComments(snapshot.entityId);
        }
        break;
    }
  }

  async undo(): Promise<void> {
    const snapshot = this.snapshot.before;

    switch (this.action) {
      case 'add':
        // Undo add = delete the created comment
        const commentIdToDelete = this.createdCommentId || snapshot.commentId;
        if (commentIdToDelete) {
          await firstValueFrom(
            this.commentService.deleteComment(commentIdToDelete)
          );
        }
        // Update canvas icon if this was the first comment (now element has no comments)
        if (snapshot.wasFirstComment) {
          this.commentsStateManager.triggerNoComments(snapshot.entityId);
        }
        break;

      case 'edit':
        // Undo edit = restore previous content
        if (snapshot.commentId && snapshot.previousContent !== undefined) {
          await firstValueFrom(
            this.commentService.editComment(snapshot.commentId, snapshot.previousContent)
          );
        }
        break;

      case 'remove':
        // Undo remove = re-create the comment
        if (snapshot.parentId) {
          // This was a reply
          const result = await firstValueFrom(
            this.commentService.replyToComment(snapshot.parentId, snapshot.content)
          );
          this.createdCommentId = result.id;
        } else {
          // This was a top-level comment
          const result = await firstValueFrom(
            this.commentService.addComment(
              snapshot.entityId,
              snapshot.entityType,
              snapshot.content
            )
          );
          this.createdCommentId = result.id;
        }
        // Update canvas icon if this was the last comment (now element has a comment again)
        if (snapshot.wasLastComment) {
          this.commentsStateManager.triggerFirstCommentAdded({
            elementId: snapshot.entityId,
            elementType: snapshot.entityType
          });
        }
        break;
    }
  }

  /**
   * Get the ID of the comment (useful for tracking after creation)
   */
  getCommentId(): string | undefined {
    return this.createdCommentId || this.snapshot.after.commentId;
  }
}
